// A feedback submission (data-model.md "feedback_submissions"; instrumentation-and-ingest §5).
//
// The active seam: an app's own end users report a bug, request or vote on a feature,
// answer a microsurvey, or leave a voice note, plus founder-supplied notes. `body` is
// free text and untrusted - DATA behind the injection wall, never an instruction
// (instrumentation-and-ingest §8). Feeds feedback_themes and feature_requests (D45).
// Tenant-scoped by `accountId`; `anonUser` is a hashed id, never a real identity.
import { z } from "zod";
import { AccountId, AppId, FeatureRequestId, FeedbackSubmissionId, FeedbackThemeId, FixId } from "../ids.js";
import { NonEmptyString, Timestamp } from "./common.js";
import { FeedbackKind, FeedbackSource, Sentiment } from "./enums.js";
// `strictObject`: this is a tenant-facing contract (the POST /feedback response body,
// `FeedbackSubmitResponse`, http/feedback.ts) - the D27 wall requires every tenant-facing object to
// reject a smuggled cost/token key loudly (a parse error), not strip it silently, so the guarantee
// is uniform across all Slice-6 tenant surfaces, not just the read views.
export const FeedbackSubmission = z.strictObject({
    id: FeedbackSubmissionId,
    accountId: AccountId,
    appId: AppId,
    kind: FeedbackKind,
    source: FeedbackSource,
    /** Provenance of an imported/forwarded submission (e.g. a support-export id); null otherwise. */
    sourceRef: NonEmptyString.nullable(),
    /** Free-text body; null for a bare vote/rating with no comment. */
    body: NonEmptyString.nullable(),
    /** A microsurvey rating (scale defined per prompt); null when not a rating. */
    rating: z.int().nullable(),
    sentiment: Sentiment.nullable(),
    /** Hashed anonymous end-user id; null when not attributable. */
    anonUser: z.string().min(1).nullable(),
    /** The theme this submission clustered into (feedback_themes.id); null until clustered. */
    themeId: z.uuid().nullable(),
    createdAt: Timestamp,
});
// --- The feedback overview: themes, feature requests, the D45 escalation (Slice 6; slice-plan.md:86-91; ADR-0023) ---
/** A clustered theme's lifecycle (data-model.md:41 feedback_themes.status). Widen only through an ADR. */
export const FeedbackThemeStatus = z.enum([
    "fixed",
    "in_progress",
    "feature_queued",
    "watching",
    "praise",
]);
/**
 * A clustered feedback theme (data-model.md:41 feedback_themes; slice-plan.md:89: "clusters real
 * intake into themes and shows a theme's volume falling after a fix ships"). `count` is the
 * rolled-up volume across submissions - the closed-loop view reads this over time, never a
 * fabricated trend. `linkedFixId` is null until a fix addresses the theme (data-model.md:41
 * linked_fix_id). `sentiment` is null until the theme has enough rated submissions to aggregate
 * (mirrors `FeedbackSubmission.sentiment` being nullable, feedback.ts:25).
 */
export const FeedbackTheme = z.strictObject({
    id: FeedbackThemeId,
    accountId: AccountId,
    appId: AppId,
    title: NonEmptyString,
    count: z.int().nonnegative(),
    sentiment: Sentiment.nullable(),
    status: FeedbackThemeStatus,
    linkedFixId: FixId.nullable(),
    /** The rolling period this theme's count covers (data-model.md:41 "window (period)"). */
    window: NonEmptyString,
    createdAt: Timestamp,
});
/** A feature request's size (data-model.md:42 feature_requests.size). */
export const FeatureRequestSize = z.enum(["small", "medium", "large"]);
/**
 * A feature request's own lifecycle in the feature lane (D43; distinct from a theme's
 * `feature_queued` status, which marks that the THEME was queued as a request - this tracks the
 * request itself once it exists). Widen only through an ADR.
 */
export const FeatureRequestStatus = z.enum(["open", "queued", "building", "shipped", "declined"]);
/**
 * A feature request (data-model.md:42 feature_requests). `votes` feeds the top-of-list ranking
 * (slice-plan.md:111 "feature votes cluster and surface the top of the list"); the voting UI and
 * feature-lane handoff are Slice 8 (D43, ADR-0023 deferral 3) - this slice stores the count only.
 */
export const FeatureRequest = z.strictObject({
    id: FeatureRequestId,
    accountId: AccountId,
    appId: AppId,
    title: NonEmptyString,
    votes: z.int().nonnegative(),
    status: FeatureRequestStatus,
    size: FeatureRequestSize,
    createdAt: Timestamp,
});
/** Which lane a D45 escalation routes to ("becomes shift or watch intake", decisions-adr.md D45). */
export const EscalationLane = z.enum(["shift", "watch"]);
/**
 * THE D45 NEGATIVE-FEEDBACK ESCALATION WALL. A discriminated union on `escalated`: a single
 * negative item stays `escalated: false` (pulsed lightly, no alarm); when negative feedback
 * clusters on one theme (by recurrence or a spike), it becomes `escalated: true` and additionally
 * carries the destination lane and a reason. BOTH members carry `themeId` (the triad's UX
 * reconciliation) so every escalation record - escalated or not - attributes to its theme; a
 * `FeedbackOverview.escalations` list can otherwise not say which theme a non-escalated record is
 * about. An escalated record cannot omit its lane/reason, and a non-escalated record cannot
 * smuggle one in - the two are distinct strict members.
 */
export const Escalation = z.discriminatedUnion("escalated", [
    z.strictObject({
        escalated: z.literal(true),
        themeId: FeedbackThemeId,
        routedToLane: EscalationLane,
        reason: NonEmptyString,
    }),
    z.strictObject({
        escalated: z.literal(false),
        themeId: FeedbackThemeId,
    }),
]);
/**
 * THE FEEDBACK OVERVIEW ("Voice of your users", Nightcrew_Bootstrap_Blueprint.md:113;
 * slice-plan.md:86-91): clustered themes, the top-voted feature requests, and each theme's
 * escalation state (D45). DERIVED, never stored - composed from `feedback_themes` and
 * `feature_requests` for the read surface (mirrors `KnowledgeDoc`, ports/knowledge.ts:94).
 */
export const FeedbackOverview = z.strictObject({
    appId: AppId,
    themes: z.array(FeedbackTheme),
    topRequests: z.array(FeatureRequest),
    escalations: z.array(Escalation),
});
//# sourceMappingURL=feedback.js.map